// API Service for Value Investing Analysis (Rules v2)

// In production with nginx proxy, use /api prefix
// In development, connect directly to backend
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? "/api" : "http://localhost:5000");

/* ------------------------------------------------------------------ */
/* Shared types                                                       */
/* ------------------------------------------------------------------ */
export interface Stock {
  ticker: string;
  company_name: string;
  sector: string;
  market_cap_cr: number;
}

export interface StockMeta {
  ticker: string;
  company_name: string;
  market_cap_cr: number | string;
  market_cap_category: string;
  sector: string;
  industry?: string;
  data_date?: string;
}

export interface Band {
  id: string;
  label?: string;
  colour?: string;
  note?: string | null;
  priority?: string;
  condition?: string;
}

export interface SectorContext {
  override_key: string | null;
  suppressed_metrics: string[];
  substitute_metrics: string[];
  reason: string;
}

export interface ExpectedPortfolio {
  positions?: number[];
  requires?: string;
}

/* ------------------------------------------------------------------ */
/* Two-track scoring                                                   */
/* ------------------------------------------------------------------ */
export type MetricStatus = "ok" | "insufficient_data" | "suppressed" | "not_assessed";

export interface MetricRow {
  id: string;
  label?: string;
  value: number | Record<string, any> | null;
  direction?: string;
  score: number | null;
  status: MetricStatus;
  reason?: string | null;
}

export interface DimensionScore {
  id: string;
  label: string;
  weight: number;
  score: number | null;
  metrics_used: number;
  metrics_total: number;
  metrics: MetricRow[];
}

export interface TrackScore {
  track: string;
  label?: string;
  suited_to?: string;
  composite: number | null;
  band: Band;
  min_dimension_score: number | null;
  expected_portfolio?: ExpectedPortfolio;
  dimensions: DimensionScore[];
}

export interface GateCheck {
  id: string;
  passed: boolean | null;
  detail?: string;
}

export interface DisqualifierCheck {
  id: string;
  label?: string;
  severity?: "hard" | "soft";
  status: "cleared" | "failed" | "not_assessed";
  detail?: string;
  reason?: string;
}

export interface DisqualifiersSummary {
  gate_passed: boolean;
  hard_failures: string[];
  soft_flags: string[];
  not_assessed: string[];
  checks: DisqualifierCheck[];
}

export interface GateResult {
  passed: boolean;
  checks: GateCheck[];
  disqualifiers: DisqualifiersSummary;
}

export interface RiskOverlay {
  id: string;
  label: string;
  suited_to?: string;
  score: number | null;
  band: Band;
  rules_evaluable: number;
  rules_insufficient: number;
}

export interface TracksResponse extends StockMeta {
  percentiles_available: boolean;
  recommended_track: string;
  data_flags: string[];
  sector_context: SectorContext;
  gates: GateResult;
  tracks: Record<string, TrackScore>;
  risk_overlay?: RiskOverlay;
}

/* ------------------------------------------------------------------ */
/* Per-investor rule-set alignment                                     */
/* ------------------------------------------------------------------ */
export type RuleStatus = "pass" | "fail" | "insufficient_data";

export interface RuleCondition {
  metric: string;
  value: number | null;
  result: boolean | null;
  threshold: string;
}

export interface RuleResult {
  id: string;
  label: string;
  category?: string;
  metric?: string | null;
  tests?: string;
  threshold?: string;
  value: number | Record<string, any> | null;
  status: RuleStatus;
  relaxation_variant?: any;
  added_in_v2?: boolean;
  classification?: string;
  matrix_score?: number;
  note?: string | null;
  conditions?: RuleCondition[];
}

export interface RuleSet {
  id: string;
  name?: string;
  pass_rate: number;
  rules_passed: number;
  rules_failed: number;
  rules_insufficient: number;
  rules_evaluable: number;
  rules_total: number;
  rules: RuleResult[];
}

export interface InvestorAnalysis extends StockMeta {
  id: string;
  name: string;
  epithet?: string;
  track?: string;
  philosophy?: string;
  note?: string | null;
  caveat?: string | null;
  status?: string;
  sector_context?: SectorContext;
  data_flags?: string[];
  rule_sets: RuleSet[];
  error?: string;
  detail?: string;
}

export interface DisqualifiersResponse extends StockMeta, DisqualifiersSummary {}

/* ------------------------------------------------------------------ */
/* Leaderboards / metadata / portfolio                                 */
/* ------------------------------------------------------------------ */
export interface TopStock {
  ticker: string;
  company_name: string;
  sector: string;
  market_cap_cr: number;
  composite: number;
  band: string;
  band_colour: string;
  gate_passed: boolean;
}

export interface TopStocksResponse {
  track: string;
  total_stocks: number;
  scored_stocks: number;
  limit: number;
  cache_timestamp?: string;
  top_stocks: TopStock[];
}

export interface RuleSetMeta {
  id: string;
  name?: string;
}

export interface InvestorMeta {
  id: string;
  name: string;
  epithet?: string;
  track?: string;
  philosophy?: string;
  status?: string;
  rule_sets: RuleSetMeta[];
}

export interface TrackMeta {
  label: string;
  suited_to?: string;
  sources?: string[];
  expected_portfolio?: ExpectedPortfolio;
  dimensions: { id: string; label: string; weight: number }[];
}

export interface RulesMeta {
  schema_version: string;
  investors: InvestorMeta[];
  tracks: Record<string, TrackMeta>;
  bands: Band[];
  disqualifiers: { id: string; label: string; severity?: string }[];
  corrections: any[];
}

export interface AllScoresResponse extends StockMeta {
  investors: Record<
    string,
    {
      id: string;
      name: string;
      track?: string;
      rule_sets: {
        id: string;
        name?: string;
        rules_passed: number;
        rules_evaluable: number;
        rules_insufficient: number;
        pass_rate: number;
      }[];
    }
  >;
  tracks: Record<string, TrackScore> | null;
}

export interface SellRule {
  id: string;
  label?: string;
  [k: string]: any;
}

export interface PositionSizing {
  [k: string]: any;
}

/* ------------------------------------------------------------------ */
/* Investor slug helpers                                               */
/* ------------------------------------------------------------------ */
export const INVESTOR_SLUGS: Record<string, string> = {
  graham: "benjamin-graham",
  buffett: "warren-buffett",
  munger: "charlie-munger",
  klarman: "seth-klarman",
  greenblatt: "joel-greenblatt",
  marks: "howard-marks",
  pabrai: "mohnish-pabrai",
  burry: "michael-burry",
  schloss: "walter-schloss",
  lynch: "peter-lynch",
};

export const SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(INVESTOR_SLUGS).map(([id, slug]) => [slug, id]),
);

/* ------------------------------------------------------------------ */
/* API service                                                         */
/* ------------------------------------------------------------------ */
export type Market = "india" | "us";

class APIService {
  private baseURL: string;
  private market: Market = "india";

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  setMarket(market: Market): void {
    this.market = market === "us" ? "us" : "india";
  }

  getMarket(): Market {
    return this.market;
  }

  /** Append the current market as a query param (correct separator). */
  private withMarket(endpoint: string): string {
    const sep = endpoint.includes("?") ? "&" : "?";
    return `${endpoint}${sep}market=${this.market}`;
  }

  private async fetchJSON<T>(endpoint: string): Promise<T> {
    const url = this.withMarket(endpoint);
    try {
      const response = await fetch(`${this.baseURL}${url}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API Error (${url}):`, error);
      throw error;
    }
  }

  async getStocks(): Promise<Stock[]> {
    const response = await this.fetchJSON<{ total: number; stocks: Stock[] }>("/stocks");
    return response.stocks;
  }

  async getRulesMeta(): Promise<RulesMeta> {
    return this.fetchJSON<RulesMeta>("/rules-meta");
  }

  async getInvestorAnalysis(
    slug: string,
    ticker: string,
    ruleSet?: string,
  ): Promise<InvestorAnalysis> {
    const q = ruleSet ? `?rule_set=${encodeURIComponent(ruleSet)}` : "";
    return this.fetchJSON<InvestorAnalysis>(`/${slug}/${ticker}${q}`);
  }

  async getTracks(ticker: string): Promise<TracksResponse> {
    return this.fetchJSON<TracksResponse>(`/tracks/${ticker}`);
  }

  async getDisqualifiers(ticker: string): Promise<DisqualifiersResponse> {
    return this.fetchJSON<DisqualifiersResponse>(`/disqualifiers/${ticker}`);
  }

  async getAllScores(ticker: string): Promise<AllScoresResponse> {
    return this.fetchJSON<AllScoresResponse>(`/score/${ticker}`);
  }

  async getTopStocks(
    track: string = "deep_value",
    limit: number = 50,
    refresh: boolean = false,
  ): Promise<TopStocksResponse> {
    const url = `/top-stocks?track=${track}&limit=${limit}${refresh ? "&refresh=true" : ""}`;
    return this.fetchJSON<TopStocksResponse>(url);
  }

  /** URL for the full-ranking Excel export (browser downloads it directly). */
  exportRankingsUrl(): string {
    return `${this.baseURL}${this.withMarket("/export")}`;
  }

  async getSellRules(): Promise<{ sell_rules: SellRule[] }> {
    return this.fetchJSON<{ sell_rules: SellRule[] }>("/sell-rules");
  }

  async getPositionSizing(): Promise<PositionSizing> {
    return this.fetchJSON<PositionSizing>("/position-sizing");
  }

  async healthCheck(): Promise<{ status: string; service: string }> {
    return this.fetchJSON<{ status: string; service: string }>("/health");
  }

  async logUser(
    email: string,
    name: string,
    status: "LOGIN_SUCCESS" | "LOGIN_FAILED" | "LOGIN_ATTEMPT",
    detail: string = "",
  ): Promise<void> {
    try {
      await fetch(`${this.baseURL}/log-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, status, detail }),
      });
    } catch (_) {
      // Logging failure must never break the auth flow
    }
  }
}

export const apiService = new APIService();
